import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import logo from '../resources/logo.svg';
import SignInModal from './SigninModal';
import { Home, PlusSquare, MessageSquare, User, List, LogOut, LogIn, X, Menu, Search } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import SearchOverlay from './SearchOverlay';
import { useAuth } from '../AuthContext';
import { useUnreadMessages } from '../UnreadMessagesContext';
import { useSignInModal } from '../SignInModalContext';

interface HeaderProps {
  style?: React.CSSProperties,
}

const MainHeader = forwardRef<HTMLDivElement, HeaderProps>(({
  style
}, ref) => {
  const { user, token, logout } = useAuth();
  const { unreadCount } = useUnreadMessages();
  const { openSignInModal } = useSignInModal();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const headerRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const dragStartX = useRef(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [headerHeight, setHeaderHeight] = useState(0);
  // Very messy, but manually set the spacer height to 57 on mobile to prevent a "jump" on page load when the actual height is calculated
  const [spacerHeight, setSpacerHeight] = useState(window.innerWidth < 640 ? 57 : 0);
  const [headerTop, setHeaderTop] = useState(0);

  // Click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      const newHeight = headerRef.current.offsetHeight;
      if (newHeight !== headerHeight) {
        setHeaderHeight(newHeight);
        if (window.innerWidth < 640) {
          setSpacerHeight(newHeight);
        } else {
          setSpacerHeight(0);
        }
      }
    }
  }, [headerHeight]);

  const toggleScrollLock = useCallback((lock: boolean) => {
    if (lock) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
  }, []);

  useEffect(() => {
    toggleScrollLock(isMenuOpen);
    return () => {
      toggleScrollLock(false);
    };
  }, [isMenuOpen, toggleScrollLock]);

  const openMenu = () => {
    setIsMenuOpen(true);
    setMenuPosition(0);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setMenuPosition(0);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isMenuOpen) {
      setIsDragging(true);
      dragStartX.current = e.touches[0].clientX - menuPosition;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging) {
      const currentX = e.touches[0].clientX;
      const newPosition = Math.min(0, currentX - dragStartX.current);
      setMenuPosition(newPosition);
      e.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    if (isDragging) {
      setIsDragging(false);
      if (menuPosition < -50) {
        closeMenu();
      } else {
        setMenuPosition(0);
      }
    }
  };

  const updateMenuHeight = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      const newHeight = window.innerHeight;
      if (menuRef.current) {
        menuRef.current.style.setProperty('--menu-height', `${newHeight}px`);
      }
    });
  }, []);

  useEffect(() => {
    updateMenuHeight();
    window.addEventListener('resize', updateMenuHeight);
    window.addEventListener('scroll', updateMenuHeight, { passive: true });
    window.addEventListener('touchmove', updateMenuHeight, { passive: true });

    return () => {
      window.removeEventListener('resize', updateMenuHeight);
      window.removeEventListener('scroll', updateMenuHeight);
      window.removeEventListener('touchmove', updateMenuHeight);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [updateMenuHeight]);

  useEffect(() => {
    if (!isMenuOpen) {
      setMenuPosition(0);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    return () => window.removeEventListener('resize', setVH);
  }, []);

  const checkMobile = useCallback(() => {
    const isMobileView = window.innerWidth < 640;
    setIsMobile(isMobileView);
    if (!isMobileView) {
      setSpacerHeight(0);
    } else if (headerRef.current) {
      setSpacerHeight(headerRef.current.offsetHeight);
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (!isMobile || !headerRef.current) return;

    const currentScrollY = window.scrollY;
    const diff = lastScrollY.current - currentScrollY;

    setHeaderTop(prevTop => {
      let newTop = prevTop + diff;
      newTop = Math.min(0, Math.max(-headerHeight, newTop));
      return newTop;
    });

    lastScrollY.current = currentScrollY;
  }, [isMobile, headerHeight]);

  useEffect(() => {
    checkMobile();
    updateHeaderHeight();
    lastScrollY.current = window.scrollY;

    const handleResize = () => {
      checkMobile();
      updateHeaderHeight();
      lastScrollY.current = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [handleScroll, checkMobile, updateHeaderHeight]);

  useEffect(() => {
    if (headerRef.current) {
      headerRef.current.style.top = '0';
    }
  }, [headerHeight]);


  const handleSignOut = () => {
    logout();
    setIsDropdownOpen(false);
    window.location.reload();
  };

  const openSearch = () => {
    setIsSearchOpen(true);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const renderDimmingOverlay = () => {
    return (
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setIsMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    );
  };

  const renderProfileSection = () => {
    if (token) {
      return (
        <div className="flex items-center gap-x-2 font-medium text-gray-500 sm:ps-3 py-2 whitespace-nowrap relative sm:py-0 h-[84px]" ref={dropdownRef}>
          <span className="left-0 top-0 bottom-0 bg-gray-300 w-[0.5px] h-[20px]"></span>
          <button
            onClick={toggleDropdown}
            className="flex items-center gap-x-2 sm:ms-4 focus:outline-none group"
          >
            <div className="relative inline-block size-[38px] bg-gray-100 rounded-full overflow-hidden">
              {user?.profilePictureUrl ? (
                <img
                  src={user.profilePictureUrl}
                  alt="Profile"
                  className="w-full h-full object-cover rounded-full transition-opacity group-hover:opacity-90"
                  onError={(e) => {
                    (e.target as HTMLImageElement).onerror = null;
                    (e.target as HTMLImageElement).src = '';
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center transition-colors group-hover:bg-gray-200">
                  <User className="h-6 w-6 text-gray-600" />
                </div>
              )}
            </div>
          </button>

          {/* Custom Dropdown Menu */}
          <div
            className={`absolute right-0 top-[90%] w-48 bg-white border border-gray-300 rounded-lg shadow-lg transform transition-all duration-200 ease-in-out ${isDropdownOpen
                ? 'opacity-100 translate-y-0 visible'
                : 'opacity-0 -translate-y-2 invisible'
              }`}
          >
            <div className="py-2">
              <button
                onClick={() => openSignInModal('/manage-listings')} 
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors"
              >
                <List className="h-4 w-4" />
                Manage listings
              </button>
              <button
                onClick={handleSignOut}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-x-2 font-medium text-gray-500 ps-3 py-2 whitespace-nowrap relative h-[84px]">
        <span className="left-0 top-0 bottom-0 bg-gray-300 w-[0.5px] h-[20px]"></span>
        <button
          onClick={() => openSignInModal('/')} 
          className="flex items-center gap-x-2 ms-4 cursor-pointer hover:text-gray-600 transition-colors"
        >
          <User className="h-4 w-4" />
          Sign in
        </button>
      </div>
    );
  };

  return (
    <>
      <div
        ref={(el) => {
          headerRef.current = el;
          if (typeof ref === 'function') {
            ref(el);
          } else if (ref) {
            ref.current = el;
          }
        }}
        className={`z-40 text-sm py-3 sm:py-0 bg-white ${isMobile ? 'fixed left-0 right-0' : 'fixed left-0 right-0 top-0'}`}
        style={{
          ...style,
          width: '100%',
          top: isMobile ? `${headerTop}px` : '0',
          borderBottom: '1px solid rgba(209, 213, 219, 0.5)',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
        }}
      >
        <nav className="relative max-w-7xl w-full mx-auto sm:px-4 sm:flex sm:items-center sm:h-[84px]" aria-label="Navbar">
          {isMobile ? (
            <div className="flex items-center justify-between px-2">
              <button
                type="button"
                className="flex items-center justify-center text-[#808080] focus:outline-none transition-colors duration-200 relative"
                onClick={openMenu}
                aria-label="Toggle navigation"
              >
                <Menu className="h-8 w-8" strokeWidth={2.5} />
                {unreadCount > 0 && (
                  <div className="absolute -top-1 -right-1 bg-[#24a1f2] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </div>
                )}
              </button>
              <div className="absolute left-1/2 transform -translate-x-1/2">
                <Link to="/" className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent opacity-50 blur-sm"></div>
                  <img src={logo} alt="Website Logo" height="25" className="relative max-w-none h-10" />
                </Link>
              </div>
              <Link
                to="/search-results?localities="
                className="flex items-center justify-center text-[#808080] focus:outline-none transition-colors duration-200"
                //onClick={openSearch}
                aria-label="Open search"
              >
                <Search className="h-8 w-8" strokeWidth={2.5} />
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center">
                <Link to="/" className="flex-none">
                  <img src={logo} alt="Website Logo" height="25" className="max-w-none h-10" />
                </Link>
              </div>

              <div className="flex items-center justify-end flex-1">
                <div className="flex items-center">
                  <Link 
                    to="/search-results?localities=" 
                    className="py-3 px-3 font-medium text-gray-500 hover:text-gray-400 whitespace-nowrap cursor-pointer flex items-center"
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Search Rooms
                  </Link>
                  <button onClick={() => openSignInModal('/create-listing')} className="py-3 px-3 font-medium text-gray-500 hover:text-gray-400 whitespace-nowrap cursor-pointer">
                    Create Listing
                  </button>
                  <button onClick={() => openSignInModal('/chat')} className="py-3 px-3 font-medium text-gray-500 hover:text-gray-400 whitespace-nowrap cursor-pointer relative">
                    Messages
                    {unreadCount > 0 && (
                      <div className="absolute -top-1 -right-1 bg-[#24a1f2] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </div>
                    )}
                  </button>
                  {renderProfileSection()}
                </div>
              </div>
            </>
          )}
        </nav>
      </div>

      <div style={{ height: isMobile ? `${spacerHeight}px` : '84px' }} />

      {/* Mobile Slide-in Menu */}
      <div
        ref={menuRef}
        className={`fixed inset-y-0 left-0 z-[60] w-4/5 max-w-[80%] bg-white shadow-lg transform transition-transform duration-300 ease-in-out flex flex-col`}
        style={{
          transform: `translateX(${isMenuOpen ? `${menuPosition}px` : '-100%'})`,
          transition: isDragging ? 'none' : 'transform 300ms ease-in-out',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex-shrink-0 flex items-center justify-between p-6">
          <Link to="/" onClick={closeMenu}>
            <img src={logo} alt="Website Logo" height="25" className="max-w-none h-10" />
          </Link>
          <button
            onClick={closeMenu}
            className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-200 rounded-full p-1"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <nav className="flex-grow overflow-y-auto px-4 py-6 pb-24">
          <button onClick={() => { navigate('/'); setIsMenuOpen(false); }} className="p-4 flex items-center text-lg text-gray-800 hover:bg-gray-100 w-full focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out">
            <Home className="h-6 w-6 mr-4 text-gray-600" />
            Home
          </button>

          <button onClick={() => { navigate('/search-results?localities='); setIsMenuOpen(false); }} className="p-4 flex items-center text-lg text-gray-800 hover:bg-gray-100 w-full focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out">
            <Search className="h-6 w-6 mr-4 text-gray-600" />
            Search Rooms
          </button>

          <button onClick={() => openSignInModal('/create-listing')}  className="p-4 flex items-center text-lg text-gray-800 hover:bg-gray-100 w-full focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out">
            <PlusSquare className="h-6 w-6 mr-4 text-gray-600" />
            Create Listing
          </button>

          <button onClick={() => openSignInModal('/chat')}  className="p-4 flex items-center text-lg text-gray-800 hover:bg-gray-100 w-full focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out relative">
            <MessageSquare className="h-6 w-6 mr-4 text-gray-600" />
            Messages
            {unreadCount > 0 && (
              <div className="ml-2 bg-[#24a1f2] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </div>
            )}
          </button>

          {token ? (
            <>
              <button onClick={() => openSignInModal('/manage-listings')} className="p-4 flex items-center text-lg text-gray-800 hover:bg-gray-100 w-full focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out">
                <List className="h-6 w-6 mr-4 text-gray-600" />
                Manage listings
              </button>

              <button onClick={handleSignOut} className="p-4 flex items-center text-lg text-gray-800 hover:bg-gray-100 w-full focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out">
                <LogOut className="h-6 w-6 mr-4 text-gray-600" />
                Sign out
              </button>
            </>
          ) : (
            <button onClick={() => openSignInModal('/')} className="p-4 flex items-center text-lg text-gray-800 hover:bg-gray-100 w-full focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out">
              <LogIn className="h-6 w-6 mr-4 text-gray-600" />
              Sign in
            </button>
          )}
        </nav>
        {token && (
          <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 p-4 bg-white">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {user?.profilePictureUrl ? (
                  <img className="h-10 w-10 rounded-full" src={user?.profilePictureUrl} alt="User" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                    <User className="h-6 w-6 text-gray-600" />
                  </div>
                )}
              </div>
              <div className="ml-3">
                <p className="text-base font-medium text-gray-700">{user?.userName || 'User'}</p>
                <p className="text-sm font-medium text-gray-500 truncate">
                  {user?.userEmail || 'user@example.com'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Overlay for mobile menu */}
      {renderDimmingOverlay()}

      {/* Search Overlay */}
      {isSearchOpen && <SearchOverlay isOpen={isSearchOpen} onClose={closeSearch} />}
    </>
  );
});

export default MainHeader;